import React from 'react';
import {
  Table,
  Tag,
  Rate,
  Input,
  Button,
  Checkbox,
  Radio,
  Space,
} from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { MasterConfigs } from '../../../interfaces/entity';

const TeamLeadPerformance: React.FC = () => {
  const config: MasterConfigs = {
    title: 'Team Lead Performance table',
    searchKeys: ['teamLead'],
    getColumns: () => [
      {
        title: 'Team Lead',
        dataIndex: 'teamLead',
        key: 'teamLead',
        fixed: 'left',
        width: 220,
        render: (name: string) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
              {name
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </div>
            <div>
              <div className="font-medium text-gray-800">{name}</div>
              <div className="text-[10px] text-gray-400 uppercase">
                AG-0000
              </div>
            </div>
          </div>
        ),
      },
      {
        title: 'Department',
        dataIndex: 'department',
        width: 180,
      },
      {
        title: 'Work Assigned',
        dataIndex: 'assigned',
        align: 'center',
        width: 140,
      },
      {
        title: 'Completed Work',
        dataIndex: 'completed',
        align: 'center',
        width: 160,
      },
      {
        title: 'On-Time %',
        dataIndex: 'onTime',
        align: 'center',
        width: 120,
        render: (val: number) => (
          <span className={val < 60 ? 'text-red-500' : 'text-gray-700'}>
            {val}%
          </span>
        ),
      },
      {
        title: 'Delayed',
        dataIndex: 'delayed',
        align: 'center',
        width: 100,
      },
      {
        title: 'Performance Score',
        dataIndex: 'score',
        align: 'center',
        width: 160,
        render: (val: number) => <span>≤ {val}</span>,
      },
      {
        title: 'Performance Status',
        dataIndex: 'status',
        align: 'center',
        width: 160,
        render: (status: string) => {
          const styles: Record<string, string> = {
            Excellent: 'text-green-600 bg-green-50 border-green-200',
            Good: 'text-orange-500 bg-orange-50 border-orange-200',
            Average: 'text-orange-400 bg-orange-50 border-orange-100',
            Poor: 'text-red-500 bg-red-50 border-red-200',
          };
          return (
            <Tag
              className={`rounded-md px-3 py-0.5 border text-xs font-medium ${styles[status]}`}
            >
              {status}
            </Tag>
          );
        },
      },
      {
        title: 'Rating',
        dataIndex: 'rating',
        align: 'center',
        width: 120,
        render: (val: number) => (
          <div className="flex items-center justify-center gap-1">
            <Rate disabled count={1} defaultValue={1} />
            <span className="font-medium text-gray-700">{val}</span>
          </div>
        ),
      },
      {
        title: 'Feedback Count',
        dataIndex: 'feedback',
        align: 'center',
        width: 150,
        render: (val: number) => (
          <a className="text-blue-500 font-medium hover:underline">
            {val}
          </a>
        ),
      },
    ],
  };

  const dataSource = Array.from({ length: 10 }).map((_, i) => ({
    key: i,
    teamLead: 'Sarah Lee',
    department: 'Department Name',
    assigned: 25 - (i % 3),
    completed: i === 2 ? 12 : 23,
    onTime: i === 2 ? 55 : 88,
    delayed: i % 3,
    score: i === 2 ? 400 : 700,
    status: [
      'Good',
      'Good',
      'Poor',
      'Excellent',
      'Poor',
      'Excellent',
      'Good',
      'Average',
      'Average',
      'Average',
    ][i],
    rating: i === 2 ? 3.2 : 4.2,
    feedback: i % 2,
  }));

  return (
    <div className="mt-4 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-lg font-semibold text-gray-700">
          {config.title}
        </h1>
        <div className="flex gap-2">
          <Input
            placeholder="Search"
            prefix={<SearchOutlined className="text-gray-400" />}
            className="w-64"
          />
          <Button icon={<FilterOutlined />} />
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex gap-4 h-[calc(100vh-140px)]">
        {/* Filter Sidebar */}
        <div className="w-64 bg-white rounded-lg shadow-sm p-4 flex flex-col h-full">
          <div className="flex justify-between items-center mb-3 border-b pb-2">
            <span className="font-semibold text-gray-700">Filter</span>
            <span className="text-gray-300 cursor-pointer">✕</span>
          </div>

          <div className="flex-1 overflow-y-auto">
            <Input
              placeholder="Search"
              size="small"
              suffix={<SearchOutlined />}
              className="mb-4"
            />

            <section className="mb-6">
              <h4 className="text-xs font-semibold text-gray-500 mb-2 uppercase">
                Department
              </h4>
              <Space direction="vertical">
                {['Sales', 'Tech', 'HR', 'Operations'].map((dept) => (
                  <Checkbox key={dept}>{dept}</Checkbox>
                ))}
              </Space>
            </section>

            <section>
              <h4 className="text-xs font-semibold text-gray-500 mb-2 uppercase">
                Performance Status
              </h4>
              <Radio.Group className="flex flex-col gap-2">
                {['Excellent', 'Good', 'Average', 'Poor'].map((s) => (
                  <Radio key={s} value={s}>
                    {s}
                  </Radio>
                ))}
              </Radio.Group>
            </section>
          </div>

          <div className="flex gap-2 border-t pt-4">
            <Button className="flex-1">Reset</Button>
            <Button type="primary" className="flex-1">
              Apply
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden">
          <Table
            columns={config.getColumns?.()}
            dataSource={dataSource}
            size="middle"
            pagination={{
              pageSize: 10,
              position: ['bottomRight'],
              showSizeChanger: false,
            }}
            scroll={{
              x: 1400,
              y: 'calc(100vh - 240px)',
            }}
            className="
              [&_.ant-table-thead_th]:text-xs
              [&_.ant-table-thead_th]:font-semibold
              [&_.ant-table-thead_th]:text-gray-500
              [&_.ant-table-cell]:py-3
            "
          />
        </div>
      </div>
    </div>
  );
};

export default TeamLeadPerformance;
